import type { ICreatePurchaseRequestBody } from '~/types/app-purchase'
import type { IAppUser, IAppUserRequest, IResendAppsUserVerificationMailPayload, USER_TYPE } from '~/types/app-user'
import type { IAccessTokenResponse } from '~/types/authentication'
import type { DeviceType } from '~/types/device-type'

import config from '~/config'
import { IAP_TYPE } from '~/types/iap'
import { request } from '~/utils/request'

export const getAppUserDetails = (env: string, userId?: string): Promise<IAppUser> => {
  return request({
    url: `${config.APP_USER_API}/env/${env}/user/${userId}/overview`,
  })
}

export const searchUsers = (id: string): Promise<IAppUserRequest> => {
  return request({
    params: {
      id,
    },
    url: `${config.APP_USER_API}/user/search`,
  })
}

export const resendAppsUserVerificationMail = (data: IResendAppsUserVerificationMailPayload): Promise<void> => {
  return request({
    data: {
      app_key: data.appKey,
      device_type: data.deviceType,
      email: data.email,
    },
    method: 'POST',
    url: `${config.APP_USER_API}/env/${data.env}/user/${data.userId}/resend-verification-mail`,
  })
}

export const updateAppsUserMail = (data: {
  clientId: string
  currentEmail: string
  env: USER_TYPE
  newEmail: string
  userId: string
}): Promise<void> => {
  return request({
    data: { client_id: data.clientId, current_email: data.currentEmail, new_email: data.newEmail },
    method: 'PUT',
    url: `${config.APP_USER_API}/env/${data.env}/user/${data.userId}/mail`,
  })
}

export const updateAppsUserPassword = (data: { env: USER_TYPE; password: string; user_id: string }): Promise<void> => {
  return request({
    data: { password: data.password },
    method: 'PUT',
    url: `${config.APP_USER_API}/env/${data.env}/user/${data.user_id}/password`,
  })
}

export const verifyAppsUserMail = (data: {
  clientId: string
  email: string
  env: USER_TYPE
  userId: string
}): Promise<void> => {
  return request({
    data: {
      client_id: data.clientId,
      email: data.email,
    },
    method: 'PUT',
    url: `${config.APP_USER_API}/env/${data.env}/user/${data.userId}/verify-mail`,
  })
}

export const getAccessToken = (data: {
  appKey: string
  deviceLanguage: string
  deviceType: DeviceType
  env: USER_TYPE
  installationId: string
  userId: string
}): Promise<IAccessTokenResponse> => {
  return request({
    data: {
      app_key: data.appKey,
      device_language: data.deviceLanguage,
      device_type: data.deviceType,
      installation_id: data.installationId,
    },
    method: 'POST',
    url: `${config.APP_USER_API}/env/${data.env}/user/${data.userId}/token`,
  })
}

export const createAppUserPurchase = ({
  appKey,
  deviceType,
  env,
  expirationDate,
  sku,
  type,
  userId,
}: ICreatePurchaseRequestBody): Promise<void> => {
  return request({
    data: {
      app_key: appKey,
      device_type: deviceType,
      ...(type === IAP_TYPE.SUBSCRIPTION && { expiration_date: expirationDate }),
      sku,
      type,
    },
    method: 'POST',
    url: `${config.APP_USER_API}/env/${env}/user/${userId}/purchase`,
  })
}

export const updateAppsUserPurchase = (data: {
  env: USER_TYPE
  expirationDate: string
  purchaseId: string
  userId: string
}): Promise<void> => {
  return request({
    data: { expiration_date: data.expirationDate },
    method: 'PUT',
    url: `${config.APP_USER_API}/env/${data.env}/user/${data.userId}/purchase/${data.purchaseId}`,
  })
}

export const deleteAppsUserPurchase = (data: { env: USER_TYPE; purchaseId: string; userId: string }): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.APP_USER_API}/env/${data.env}/user/${data.userId}/purchase/${data.purchaseId}`,
  })
}

export const deleteAppUserAccount = (data: { env: USER_TYPE; userId: string }): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.APP_USER_API}/env/${data.env}/user/${data.userId}`,
  })
}
